/** Planificación de producción: solicitudes de producto y códigos de OP. */

import {
  getActiveProductionCatalogOptions,
  getProductionCatalogColorMap,
} from './productionCatalogDefaults';

export const REQUEST_ORIGINS = ['Comercial', 'Bodega PT', 'Stock mínimo', 'Muestra', 'Reposición'];

export const REQUEST_STATUS = {
  requested: 'Solicitado',
  planned: 'Planificada',
  inProcess: 'En Proceso',
  finished: 'Terminada',
  delivered: 'Entregada',
  rejected: 'Rechazada',
  rescheduling: 'En Reprogramación',
  cancelled: 'Cancelada',
};

/** Avance de cada producto dentro de una solicitud aceptada. */
export const ITEM_PROGRESS = {
  planned: 'Planificado',
  inFabrication: 'En Fabricación',
  finished: 'Terminado',
};

export const ITEM_PROGRESS_OPTIONS = [
  ITEM_PROGRESS.planned,
  ITEM_PROGRESS.inFabrication,
  ITEM_PROGRESS.finished,
];

export const REQUEST_STATUSES = [
  REQUEST_STATUS.requested,
  REQUEST_STATUS.planned,
  REQUEST_STATUS.inProcess,
  REQUEST_STATUS.finished,
  REQUEST_STATUS.delivered,
  REQUEST_STATUS.rejected,
  REQUEST_STATUS.rescheduling,
  REQUEST_STATUS.cancelled,
];

export const REQUEST_REJECTION_REASONS = [
  'Falta de materia prima',
  'Fórmula o color no disponible',
  'Capacidad de producción insuficiente',
  'Fecha límite no factible',
  'Información incompleta o incorrecta',
  'Solicitud duplicada',
  'Producto no fabricable',
  'Otro',
];

export const REQUEST_STATUS_COLORS = {
  [REQUEST_STATUS.requested]: '#64748b',
  [REQUEST_STATUS.planned]: '#0ea5e9',
  [REQUEST_STATUS.inProcess]: '#d97706',
  [REQUEST_STATUS.finished]: '#15803d',
  [REQUEST_STATUS.delivered]: '#0f766e',
  [REQUEST_STATUS.rejected]: '#dc2626',
  [REQUEST_STATUS.rescheduling]: '#9333ea',
  [REQUEST_STATUS.cancelled]: '#71717a',
};

/** Orígenes activos desde Producción → Ajustes (fallback a defaults). */
export const getActiveRequestOrigins = () => {
  const options = getActiveProductionCatalogOptions('product_request_origin');
  const labels = options.map((opt) => opt.value).filter(Boolean);
  return labels.length ? labels : REQUEST_ORIGINS;
};

/** Estados activos de solicitud T-COAT desde Producción → Ajustes. */
export const getActiveRequestStatuses = () => {
  const options = getActiveProductionCatalogOptions('product_request_status');
  const labels = options.map((opt) => opt.value).filter(Boolean);
  return labels.length ? labels : REQUEST_STATUSES;
};

/** Motivos de rechazo activos desde Producción → Ajustes. */
export const getActiveRequestRejectionReasons = () => {
  const options = getActiveProductionCatalogOptions('product_request_rejection_reason');
  const labels = options.map((opt) => opt.value).filter(Boolean);
  return labels.length ? labels : REQUEST_REJECTION_REASONS;
};

/** Avance de ítems activo desde Producción → Ajustes. */
export const getActiveItemProgressOptions = () => {
  const options = getActiveProductionCatalogOptions('product_request_item_progress');
  const labels = options.map((opt) => opt.value).filter(Boolean);
  return labels.length ? labels : ITEM_PROGRESS_OPTIONS;
};

/** Colores de estado: defaults + overrides de Producción → Ajustes. */
export const getRequestStatusColorMap = () => ({
  ...REQUEST_STATUS_COLORS,
  ...getProductionCatalogColorMap('product_request_status'),
});

export const getRequestStatusColor = (status) =>
  getRequestStatusColorMap()[String(status || '').trim()] || '#64748b';

export const PROCESS_STAGES = {
  planning: 'Planificación',
  fabricacion: 'Fabricación',
  tintometria: 'Tintometría',
  envasado: 'Envasado',
};

export const PLANNING_CHANGED_EVENT = 'sqmt-production-changed';

const todayIso = () => new Date().toISOString().slice(0, 10);

export const emptyProductRequest = () => ({
  id: null,
  code: '',
  origin: 'Comercial',
  executive: '',
  requester: '',
  product_code: '',
  product_name: '',
  color_code: '',
  color_name: '',
  formula_kind: 'color',
  quantity: 100,
  unit: 'L',
  needed_by: todayIso(),
  needed_by_time: '',
  status: 'Borrador',
  op_code: '',
  sale_code: '',
  quotation_code: '',
  items_count: 0,
  notes: '',
});

const isoDate = (value) => (value ? String(value).slice(0, 10) : '');

/** Nombre del ejecutivo comercial que generó la solicitud (columna Origen en UI). */
const executiveFromApi = (row) => {
  const createdBy = String(row.created_by || '').trim();
  if (createdBy && createdBy.toLowerCase() !== 'system') return createdBy;
  return '';
};

export const particularNote = (notes) => String(notes || '').trim();

const qty = (value) => Number(value || 0);

/** Fecha límite "dd-mm-aaaa hh:mm" (sin hora si no se ha fijado). */
export const formatDeadline = (date, time) => {
  const iso = String(date || '').slice(0, 10);
  if (!iso) return '—';
  const parsed = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return '—';
  const day = parsed.toLocaleDateString('es-CL');
  return time ? `${day} ${String(time).slice(0, 5)}` : day;
};

/** Fecha/hora ISO → "dd-mm-aaaa, hh:mm" (zona local). */
export const formatDateTimeEs = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** Envases por formato: 1/4 galón / galón / tineta 16 L / tineta 20 L. */
export const formatContainerBreakdown = (row) =>
  [row.qty_quarter_gallon, row.qty_gallon, row.qty_tineta_16, row.qty_tineta_20]
    .map((value) => String(Math.round(qty(value) * 100) / 100))
    .join('/');

/** Cabecera de solicitud del backend -> fila de la tabla de planificación. */
export const mapApiRequestToRow = (row) => ({
  id: `req-${row.id}`,
  request_id: row.id,
  code: row.request_code || '',
  origin: row.origin || '',
  executive: executiveFromApi(row),
  created_by: row.created_by || '',
  requester: row.reference || '',
  product_code: '',
  product_name: row.primary_product_name || '',
  items_count: Number(row.items_count || 0),
  needed_by: isoDate(row.needed_by_date),
  needed_by_time: row.needed_by_time || '',
  deadline_key: `${isoDate(row.needed_by_date)} ${row.needed_by_time || ''}`.trim(),
  status: row.status || '',
  rejection_reason: row.rejection_reason || '',
  op_code: row.op_code || '',
  quotation_code: row.quotation_code || '',
  sale_code: row.sale_code || '',
  organization_name: row.organization_name || '',
  qty_quarter_gallon: qty(row.qty_quarter_gallon),
  qty_gallon: qty(row.qty_gallon),
  qty_tineta_16: qty(row.qty_tineta_16),
  qty_tineta_20: qty(row.qty_tineta_20),
  qty_other_formats: qty(row.qty_other_formats),
  containers_total:
    qty(row.qty_quarter_gallon) + qty(row.qty_gallon) + qty(row.qty_tineta_16) + qty(row.qty_tineta_20),
  particular_note: particularNote(row.notes),
  notes: row.notes || '',
  source: row.source || '',
  finished_at: row.finished_at || null,
  created_at: isoDate(row.created_at),
  storage: 'api',
});

/** Ítem de solicitud del backend -> fila de la tabla de planificación. */
export const mapApiRequestItemToRow = (row) => ({
  id: `item-${row.id}`,
  item_id: row.id,
  request_id: row.request_id,
  code: row.request_code || '',
  origin: row.origin || '',
  executive: executiveFromApi(row),
  created_by: row.created_by || '',
  requester: row.reference || '',
  product_code: row.product_code || '',
  product_name: row.product_name || '',
  color_code: row.color_code || '',
  color_name: row.color_name || '',
  formula_kind: row.formula_kind || 'color',
  quantity: Number(row.quantity || 0),
  unit: row.unit || 'L',
  progress_status: String(row.progress_status || ITEM_PROGRESS.planned).trim() || ITEM_PROGRESS.planned,
  finished_at: row.finished_at || null,
  needed_by: isoDate(row.needed_by_date),
  needed_by_time: row.needed_by_time || '',
  deadline_key: `${isoDate(row.needed_by_date)} ${row.needed_by_time || ''}`.trim(),
  status: row.request_status || '',
  op_code: row.op_code || '',
  quotation_code: row.quotation_code || '',
  sale_code: row.sale_code || '',
  created_at: isoDate(row.created_at),
  storage: 'api',
});

export const inferProcessStage = (row) => {
  if (row?.process_stage) return row.process_stage;
  const status = String(row?.status || '');
  if (status === 'Borrador' || status === 'Planificada' || status === 'Anulada') return 'planning';
  const isColor =
    row?.formula_kind === 'color' ||
    String(row?.formula_code || '').startsWith('FO-C-') ||
    ['A006', 'A012'].includes(row?.product_code);
  if (status === 'En calidad') return isColor ? 'tintometria' : 'envasado';
  if (status === 'Fabricado' || status === 'Terminado') return 'envasado';
  if (status === 'Reproceso') return 'fabricacion';
  return 'fabricacion';
};
